// Product Swiper Carousel Handler
class ProductSwiperHandler {
  constructor() {
    this.swipers = new Map();
    this.init();
  }

  init() {
    // Wait for DOM and Swiper to be ready
    if (typeof Swiper === 'undefined') {
      console.warn('Swiper is not loaded. Please include Swiper CSS and JS files.');
      return;
    }

    this.initializeAllSwipers();
    this.bindEvents();
  }

  initializeAllSwipers() {
    const productGridItems = document.querySelectorAll('[data-product-block]');
    
    productGridItems.forEach((item) => {
      const productId = item.dataset.productId;
      const mainSwiper = item.querySelector('.product-swiper:not(.product-swiper-thumbs)');
      const thumbsSwiper = item.querySelector('.product-swiper-thumbs');
      
      if (mainSwiper && thumbsSwiper) {
        this.initializeSwiper(productId, mainSwiper, thumbsSwiper);
      }
    });
  }

  initializeSwiper(productId, mainElement, thumbsElement) {
    const uniqueId = mainElement.id;
    const thumbsId = thumbsElement.id;
    
    // Initialize thumbnails swiper first
    const thumbsSwiper = new Swiper(`#${thumbsId}`, {
      spaceBetween: 8,
      slidesPerView: 3,
      freeMode: true,
      watchSlidesProgress: true,
      slideToClickedSlide: true,
      breakpoints: {
        480: {
          slidesPerView: 3,
          spaceBetween: 8
        },
        768: {
          slidesPerView: 3,
          spaceBetween: 10
        },
        1024: {
          slidesPerView: 3,
          spaceBetween: 12
        }
      },
      on: {
        init: function() {
          // Ensure first slide is active
          if (this.slides.length > 0) {
            this.slides[0].classList.add('swiper-slide-thumb-active');
          }
        }
      }
    });

    // Initialize main swiper
    const mainSwiper = new Swiper(`#${uniqueId}`, {
      spaceBetween: 0,
      effect: 'fade',
      fadeEffect: {
        crossFade: true
      },
      navigation: {
        nextEl: `.swiper-button-next-${uniqueId.replace('swiper-', '').replace('-main', '')}`,
        prevEl: `.swiper-button-prev-${uniqueId.replace('swiper-', '').replace('-main', '')}`,
      },
      thumbs: {
        swiper: thumbsSwiper,
        multipleActiveThumbs: false
      },
      loop: false,
      speed: 300,
      allowTouchMove: true,
      on: {
        init: function() {
          // Hide navigation if only one slide
          if (this.slides.length <= 1) {
            const nextBtn = mainElement.querySelector('.swiper-button-next');
            const prevBtn = mainElement.querySelector('.swiper-button-prev');
            if (nextBtn) nextBtn.style.display = 'none';
            if (prevBtn) prevBtn.style.display = 'none';
          }
        },
        slideChange: function() {
          // Update thumbnail active state
          thumbsSwiper.slides.forEach((slide, index) => {
            slide.classList.toggle('swiper-slide-thumb-active', index === this.activeIndex);
          });
        }
      }
    });

    // Store swiper instances
    this.swipers.set(productId, {
      main: mainSwiper,
      thumbs: thumbsSwiper
    });

    // Handle thumbnail clicks manually for better control
    thumbsElement.addEventListener('click', (e) => {
      const clickedSlide = e.target.closest('.swiper-slide');
      if (clickedSlide) {
        const slideIndex = Array.from(thumbsSwiper.slides).indexOf(clickedSlide);
        if (slideIndex !== -1) {
          mainSwiper.slideTo(slideIndex);
        }
      }
    });
  }

  bindEvents() {
    // Handle dynamic content loading (for AJAX-loaded products)
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const productBlocks = node.querySelectorAll ? 
              node.querySelectorAll('[data-product-block]') : 
              (node.matches && node.matches('[data-product-block]') ? [node] : []);
            
            productBlocks.forEach((item) => {
              const productId = item.dataset.productId;
              if (!this.swipers.has(productId)) {
                const mainSwiper = item.querySelector('.product-swiper:not(.product-swiper-thumbs)');
                const thumbsSwiper = item.querySelector('.product-swiper-thumbs');
                
                if (mainSwiper && thumbsSwiper) {
                  this.initializeSwiper(productId, mainSwiper, thumbsSwiper);
                }
              }
            });
          }
        });
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    // Handle window resize
    window.addEventListener('resize', this.debounce(() => {
      this.swipers.forEach((swiperPair) => {
        if (swiperPair.main) swiperPair.main.update();
        if (swiperPair.thumbs) swiperPair.thumbs.update();
      });
    }, 250));

    // Handle visibility change (for better performance)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.swipers.forEach((swiperPair) => {
          if (swiperPair.main) swiperPair.main.update();
          if (swiperPair.thumbs) swiperPair.thumbs.update();
        });
      }
    });
  }

  // Utility function for debouncing
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Public method to get swiper instance
  getSwiper(productId) {
    return this.swipers.get(productId);
  }

  // Public method to destroy swiper
  destroySwiper(productId) {
    const swiperPair = this.swipers.get(productId);
    if (swiperPair) {
      if (swiperPair.main) swiperPair.main.destroy(true, true);
      if (swiperPair.thumbs) swiperPair.thumbs.destroy(true, true);
      this.swipers.delete(productId);
    }
  }

  // Public method to update all swipers
  updateAllSwipers() {
    this.swipers.forEach((swiperPair) => {
      if (swiperPair.main) swiperPair.main.update();
      if (swiperPair.thumbs) swiperPair.thumbs.update();
    });
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.productSwiperHandler = new ProductSwiperHandler();
});

// For Shopify themes that use Turbo/AJAX navigation
document.addEventListener('shopify:section:load', () => {
  if (window.productSwiperHandler) {
    window.productSwiperHandler.initializeAllSwipers();
  }
});

// Export for potential module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = ProductSwiperHandler;
}